import type {
  CreateDriversHoursRuleset,
  LogLevelSetRequest,
  UpdateDriversHoursRuleset,
  CreateFleetNewsNote,
  CreatePartnerResellerRequest,
  DryRunRequest,
  FleetNewsNoteType,
  QueryDocumentInput,
  SetPartnerResellerFeatureRequest,
} from '@oip/contracts';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type {
  AddExternalRecipient,
  UpdateProviderOutbound,
  StoreProviderCredential,
  CopilotAskRequestInput,
  AdminMemberListQuery,
  CreateMembershipRequest,
  CreatePlatformUserRequest,
  UpdateMembershipRequest,
  CreateTenantRequest,
  PlatformAuditListQuery,
  PlatformUserListQuery,
  UpdatePlatformUserRequest,
  TenantListQuery,
  UpdateTenantRequest,
  AuditListQuery,
  CreatePolicyRequest,
  CreatePolicyVersionRequest,
  IncidentListQuery,
  LlmExecutionListQuery,
  CreatePromptRequest,
  CreatePromptVersionRequest,
  PromptKeyOverrideRequest,
  PolicyDefinitionInput,
  UpdatePromptVersionRequest,
  PolicyLifecycleRequest,
  PolicyListQuery,
  RecommendationListQuery,
  VehicleEventListQuery,
  CreateVehicleRequest,
  CreateCorrelationConfigVersionRequest,
  CreateDriverRequest,
  DriverListQuery,
  UpdateDriverRequest,
  UpdateVehicleRequest,
  VehicleListQuery,
  RecordOutcomeRequest,
  EvidenceAttachment,
  EvidenceType,
  OutcomeTrendBucket,
  InboundEventQuery,
  CreateProviderConnectionRequest,
  WhiteLabelListQuery,
  CreateWhiteLabelRequestInput,
  UpdateWhiteLabelRequest,
  UpdateWhiteLabelFeature,
  CreatePartnerCompanyRequest,
  UpdatePartnerBranding,
  AddPartnerMemberRequest,
  UpdatePartnerMemberRequest,
} from '@oip/contracts';
import { endpoints, logLevelsApi, logsApi, platformAlertsApi } from '../api/endpoints';
import { ApiClientError, putToPresignedUrl } from '../api/client';
import { sha256Hex } from '../pages/recommendations/evidenceOptions';
import { fleetKeys, platformKeys } from '../api/queryKeys';
import { NAV_COUNT_HOURS, type NavCounts } from '../navCounts';
import type { ListParams } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

/** Query keys are namespaced by tenant so switching tenant refetches cleanly. */
export function useTenantKey(): string {
  return useAuth().tenantId ?? 'none';
}

const useListQuery = <T>(fn: () => Promise<T>, key: readonly unknown[]) =>
  useQuery({ queryKey: key, queryFn: fn, placeholderData: keepPreviousData });

// ── incidents ───────────────────────────────────────────────────────────────

export function useIncidents(q: ListParams<IncidentListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.incidents(q), ['incidents', t, q]);
}
export function useIncident(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['incident', t, id],
    queryFn: () => endpoints.incident(id),
    enabled: !!id,
  });
}
/**
 * Ask for an AI rewording of one incident.
 *
 * ⚠️ **The incident is invalidated on EVERY outcome, including `SKIPPED`.** A refusal writes
 * nothing, so the re-read returns what is already on screen — but the alternative is a rule
 * that has to decide which outcomes wrote, in a second place, and get it right for a value
 * added later. `shouldRefetch` is what the PAGE consults to decide what to SAY; the cache
 * simply stays honest.
 *
 * ⚠️ No optimistic update. Nothing is known until the worker answers, and inventing wording
 * here would put text on the page that the server may refuse.
 */
export function useIncidentSummarise(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: () => endpoints.incidentSummarise(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['incident', t, id] });
    },
  });
}

export function useIncidentAction(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (input: { action: 'acknowledge' | 'resolve' | 'close'; note?: string }) =>
      endpoints.incidentAction(id, input.action, input.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['incident', t, id] });
      void qc.invalidateQueries({ queryKey: ['incidents', t] });
    },
  });
}
export function useIncidentAssign(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (input: { assigneeUserId: string; note?: string }) =>
      endpoints.incidentAssign(id, input.assigneeUserId, input.note),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['incident', t, id] }),
  });
}

// ── recommendations ───────────────────────────────────────────────────────

export function useRecommendations(q: ListParams<RecommendationListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.recommendations(q), ['recommendations', t, q]);
}
export function useRecommendation(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['recommendation', t, id],
    queryFn: () => endpoints.recommendation(id),
    enabled: !!id,
  });
}
export function useRecommendationHistory(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['recommendation-history', t, id],
    queryFn: () => endpoints.recommendationHistory(id),
    enabled: !!id,
  });
}
/**
 * CAN parameter history for a vehicle.
 *
 * Keyed by parameter set AND window, so changing either refetches rather than
 * relabelling the previous shape. Not polled: telemetry arrives continuously
 * and a chart that moved under the reader would make it unreadable.
 */
export function useVehicleTelemetry(
  id: string,
  parameters: string[],
  hours: number,
  resolution: 'auto' | 'raw' | 'hourly' = 'auto',
) {
  const t = useTenantKey();
  const joined = parameters.join(',');
  return useQuery({
    // `resolution` is part of the key: the same vehicle, parameters and window
    // can be fetched raw or hourly, and the two responses are not
    // interchangeable — one is samples, the other is means.
    queryKey: ['vehicle-telemetry', t, id, joined, hours, resolution],
    queryFn: () => endpoints.vehicleTelemetry(id, { parameters: joined, hours, resolution }),
    enabled: !!id && parameters.length > 0,
  });
}

/**
 * CAN health summary for one vehicle.
 *
 * Kept separate from `useVehicleTelemetry` rather than folded into it: this
 * reads a fixed set of headline parameters over two windows, while the history
 * hook reads whichever group the user picked over one. Sharing a cache key
 * between them would make selecting a chart group invalidate the summary above
 * it for no reason.
 */
export function useVehicleTelemetryHealth(id: string, hours = 24) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['vehicle-telemetry-health', t, id, hours],
    queryFn: () => endpoints.vehicleTelemetryHealth(id, { hours }),
    enabled: !!id,
  });
}

/**
 * Trend and drift for one vehicle.
 *
 * Long default stale time is deliberate: these are fitted over 30 days or more
 * from an hourly rollup that only changes once an hour, so refetching on every
 * focus would cost a month of aggregation to redraw the same lines.
 */
/**
 * Where a vehicle has been.
 *
 * Not polled. A track is read to answer "where did it go", which is a question
 * about a window that has already closed — and a map that refit its bounds every
 * thirty seconds would move under the reader while they were looking at it.
 */
export function useVehicleTrack(id: string, hours = 24) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['vehicle-track', t, id, hours],
    queryFn: () => endpoints.vehicleTrack(id, { hours }),
    enabled: !!id,
  });
}

/**
 * The vehicle's journeys for a window.
 *
 * Same posture as the track above: no polling. A trip list is a record of what
 * has already happened, and a list that reordered itself while somebody was
 * reading it would be worse than one refreshed on demand.
 */
export function useVehicleTrips(id: string, hours = 24, day?: string) {
  const t = useTenantKey();
  return useQuery({
    // ⚠️ The day is part of the KEY, or moving between days serves the first
    // day's journeys under the second day's heading.
    queryKey: ['vehicle-trips', t, id, hours, day ?? null],
    queryFn: () => endpoints.vehicleTrips(id, { hours, day }),
    enabled: !!id,
  });
}

/**
 * Drivers' hours for one vehicle.
 *
 * `enabled` on the FEATURE as well as the id, so a tenant without `TACHOGRAPH`
 * never issues a request the route would 403. The card is not rendered for
 * them either — this is the second half of the same decision, kept here so a
 * later caller cannot reintroduce the call by rendering the card elsewhere.
 */
export function useVehicleTacho(id: string, hours = 24, enabled = true) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['vehicle-tacho', t, id, hours],
    queryFn: () => endpoints.vehicleTacho(id, { hours }),
    enabled: !!id && enabled,
  });
}

/**
 * Boolean telemetry as state spans.
 *
 * The parameter list is joined into the query key as well as the request, so
 * asking for a different set is a different cache entry rather than a stale hit.
 */
export function useVehicleStates(
  id: string,
  parameters: readonly string[],
  hours = 24,
  /**
   * One whole UTC day instead of a rolling window.
   *
   * ⚠️ Part of the query KEY, or a reader moving between days would be served
   * the first day's answer under the second day's heading.
   */
  day?: string,
) {
  const t = useTenantKey();
  const joined = parameters.join(',');
  return useQuery({
    queryKey: ['vehicle-states', t, id, joined, hours, day ?? null],
    queryFn: () => endpoints.vehicleStates(id, { parameters: joined, hours, day }),
    enabled: !!id && parameters.length > 0,
  });
}

/**
 * The tenant's geofences.
 *
 * Long stale time and no polling: a zone is configuration, changed by hand and
 * rarely, and both maps that draw them refetch on a tenant switch through the
 * tenant key like every other query here.
 */
/**
 * Where the fleet is.
 *
 * Polled every 60 seconds, unlike every other map read here. A track is history
 * and settles; this answers "where is the fleet NOW", and a page left open
 * showing an hour-old snapshot with no indication of its age is the failure this
 * whole surface has to avoid. The response carries `asOf` so the screen can say
 * how fresh it is rather than implying live.
 */
export function useFleetPositions(enabled = true, asTenantId: string | null = null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['fleet-positions', asTenantId ?? t],
    queryFn: () =>
      asTenantId ? endpoints.fleetPositionsAs(asTenantId) : endpoints.fleetPositions(),
    // Positions are CAN telemetry: without the entitlement the route refuses,
    // and asking anyway turns a feature nobody bought into a visible error.
    enabled,
    refetchInterval: 60_000,
  });
}

/**
 * AI usage against the allowance.
 *
 * Polled while the card is open, because the figures move as the fleet asks
 * questions and an administrator watching a ceiling wants to see it approach
 * rather than discover it was passed. Sixty seconds is well below the coarsest
 * window (an hour) and far above the cost of the query.
 */
export function useAiAllowance() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['ai-allowance', t],
    queryFn: () => endpoints.aiAllowance(),
    refetchInterval: 60_000,
  });
}

/**
 * Drive a recommendation's lifecycle.
 *
 * PESSIMISTIC, like every governance action here. The server applies guards the
 * client cannot evaluate — the legal transition, `approvalRequired`, and whether
 * somebody else decided first — so a status that moved on screen before the
 * write landed would be claiming an outcome that may not happen.
 */
/**
 * The same action across a selection.
 *
 * Invalidates the LIST rather than a row, because a bulk result is only
 * meaningful against a refreshed queue — and refetching is how the reader sees
 * which of their selection actually moved.
 */
export function useBulkRecommendationAction() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (v: { action: string; ids: string[]; note?: string }) =>
      endpoints.bulkRecommendationAction(v.action, {
        ids: v.ids,
        ...(v.note ? { note: v.note } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recommendations', t] });
      void qc.invalidateQueries({ queryKey: ['incident', t] });
    },
  });
}

export function useRecommendationAction(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (v: { action: string; note?: string }) =>
      endpoints.recommendationAction(id, v.action, { ...(v.note ? { note: v.note } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recommendation', t, id] });
      void qc.invalidateQueries({ queryKey: ['recommendations', t] });
      // The incident's own view lists its recommendations with their statuses.
      void qc.invalidateQueries({ queryKey: ['incident', t] });
    },
  });
}

export function useGeofences() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['geofences', t],
    queryFn: () => endpoints.geofences(),
    staleTime: 10 * 60_000,
  });
}

/**
 * Classifying a zone. Invalidates rather than patching locally: the server
 * decides the counts the page reports, and a client recomputing them would be a
 * second opinion on a number it did not produce.
 */
/**
 * What the API is running. Not tenant-keyed — the answer is about the process,
 * not the caller — and cached hard, because it cannot change without a redeploy
 * and re-asking on every menu open would be noise in the network panel.
 */
export function useApiVersion() {
  return useQuery({
    queryKey: ['system-version'],
    queryFn: () => endpoints.version(),
    staleTime: Infinity,
    retry: false,
  });
}

export function useClassifyGeofence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; kind: string }) => endpoints.classifyGeofence(v.id, v.kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['geofences'] }),
  });
}

export function useVehicleTelemetryTrends(id: string, days = 30) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['vehicle-telemetry-trends', t, id, days],
    queryFn: () => endpoints.vehicleTelemetryTrends(id, { days }),
    enabled: !!id,
    staleTime: 15 * 60_000,
  });
}

/**
 * Vehicle classes — the cohorts comparison is computed inside.
 *
 * Archived classes are excluded by default: they are not offered for new
 * assignment, and a picker that listed them would let a user choose a cohort
 * the server then refuses.
 */
/**
 * Drivers'-hours rulesets, and what an UNASSIGNED vehicle is judged by.
 *
 * ⚠️ The response carries `tenantDefault` beside the list, and every caller must use it: a
 * picker whose empty option said only "None" would read as *no limits*, which is the opposite
 * of what an absent ruleset does.
 */
export function useDriversHoursRulesets(enabled = true) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['drivers-hours-rulesets', t],
    queryFn: () => endpoints.driversHoursRulesets(),
    /*
     * ⚠️ The route is gated on TACHOGRAPH, so for a tenant without it this is a request that
     * always 403s. Callers that can tell (the vehicle form, which hides the picker) pass false
     * rather than firing it and discarding the refusal — a failing request on every vehicle
     * form load is noise in the one place somebody looks when a form misbehaves.
     */
    enabled,
  });
}

/**
 * Ruleset mutations.
 *
 * PESSIMISTIC, like every other governance action — the server applies rules the client cannot
 * evaluate: name uniqueness against ARCHIVED rulesets, and the refusal to archive one that
 * still governs vehicles.
 */
export function useDriversHoursRulesetMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['drivers-hours-rulesets'] });
    // A ruleset's name appears on the vehicle, so both lists move together.
    void qc.invalidateQueries({ queryKey: ['vehicles'] });
  };
  return {
    create: useMutation({
      mutationFn: (body: CreateDriversHoursRuleset) => endpoints.createDriversHoursRuleset(body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; body: UpdateDriversHoursRuleset }) =>
        endpoints.updateDriversHoursRuleset(v.id, v.body),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: (id: string) => endpoints.archiveDriversHoursRuleset(id),
      onSuccess: invalidate,
    }),
    restore: useMutation({
      mutationFn: (id: string) => endpoints.restoreDriversHoursRuleset(id),
      onSuccess: invalidate,
    }),
  };
}

export function useVehicleClasses(includeArchived = false) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['vehicle-classes', t, includeArchived],
    queryFn: () =>
      endpoints.vehicleClasses({ includeArchived: includeArchived ? 'true' : 'false' }),
  });
}

/**
 * Class mutations.
 *
 * PESSIMISTIC, like every other governance action: the server applies rules the
 * client cannot evaluate — name uniqueness against ARCHIVED classes, and the
 * refusal to edit or assign an archived one — so an optimistic update would
 * show a change that is about to be rejected.
 */
export function useVehicleClassMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['vehicle-classes'] });
    // A rename changes the label shown on every vehicle list row.
    void qc.invalidateQueries({ queryKey: ['vehicles'] });
  };
  return {
    create: useMutation({
      mutationFn: (body: Parameters<typeof endpoints.createVehicleClass>[0]) =>
        endpoints.createVehicleClass(body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; body: Parameters<typeof endpoints.updateVehicleClass>[1] }) =>
        endpoints.updateVehicleClass(v.id, v.body),
      onSuccess: invalidate,
    }),
    archive: useMutation({
      mutationFn: (id: string) => endpoints.archiveVehicleClass(id),
      onSuccess: invalidate,
    }),
    restore: useMutation({
      mutationFn: (id: string) => endpoints.restoreVehicleClass(id),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Bulk vehicle import — the dry run and the apply.
 *
 * ⚠️ **The dry run invalidates NOTHING, and that is an assertion about the server rather
 * than an omission.** `validate` writes no row — an integration case runs it twice and
 * compares table counts — so a cache invalidation here would refetch the fleet for an
 * operation that cannot have changed it, and would quietly paper over the day that stopped
 * being true. The one that writes is the one that invalidates.
 */
export function useVehicleImport() {
  const qc = useQueryClient();
  return {
    validate: useMutation({
      mutationFn: (csv: string) => endpoints.validateVehicleImport(csv),
    }),
    apply: useMutation({
      mutationFn: (csv: string) => endpoints.applyVehicleImport(csv),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['vehicles'] });
        // The dashboard counts vehicles, and an import is the largest single move that
        // figure ever makes.
        void qc.invalidateQueries({ queryKey: ['fleet-summary-as'] });
        /*
         * ⚠️ **The refused-registration list is the interesting one.** Ingestion never
         * creates a vehicle, so a plate it could not resolve is recorded as an
         * `UnresolvedVehicleReference` and shown on the fleet page — and that list is
         * "filtered by what exists rather than cleared by a write", because creating the
         * vehicle IS the correction. An import is the BULK form of exactly that correction,
         * so without this the operator fixes forty refusals and the screen still reports
         * forty until something else happens to refetch.
         */
        void qc.invalidateQueries({ queryKey: ['unresolved-vehicles'] });
      },
    }),
  };
}

/**
 * The three HIERARCHY import surfaces — fleet accounts, a partner's customers, and the
 * platform's whole tree.
 *
 * ⚠️ **Separate from `useVehicleImport` rather than folded into it, and the INVALIDATION is
 * why.** A vehicle import moves the fleet count and the refused-registration list; a
 * hierarchy import creates TENANTS, which is a different set of stale queries entirely. One
 * hook covering both would either over-invalidate every list in the product on any import, or
 * quietly miss the one that mattered — and a list that does not refresh after a bulk create
 * reads as the import having silently done nothing.
 *
 * ⚠️ The SURFACE decides what a file may contain, and it decides it on the SERVER. Passing the
 * endpoint pair in is a choice of which URL to call, never a claim about what the caller may
 * create: the guards are the authority (D8a).
 */
export function useHierarchyImport(surface: 'TENANT' | 'PARTNER' | 'PLATFORM') {
  const qc = useQueryClient();
  // `satisfies Record<...>` rather than an index into a tuple: a surface added without both
  // halves is then a compile error instead of a call to `undefined` at the moment of apply.
  const CALLS = {
    TENANT: { validate: endpoints.validateHierarchyImport, apply: endpoints.applyHierarchyImport },
    PARTNER: { validate: endpoints.validatePartnerImport, apply: endpoints.applyPartnerImport },
    PLATFORM: { validate: endpoints.validatePlatformImport, apply: endpoints.applyPlatformImport },
  };
  const calls = CALLS[surface];
  return {
    validate: useMutation({ mutationFn: (csv: string) => calls.validate(csv) }),
    apply: useMutation({
      mutationFn: (csv: string) => calls.apply(csv),
      onSuccess: () => {
        /*
         * Everything that COUNTS or LISTS a tenant. A hierarchy import is the largest single
         * move any of these figures ever makes, and the screen the operator is standing on is
         * usually one of them — the partner console's own company tree most of all.
         */
        void qc.invalidateQueries({ queryKey: ['sub-tenants'] });
        void qc.invalidateQueries({ queryKey: ['partner-companies'] });
        void qc.invalidateQueries({ queryKey: ['partner-resellers'] });
        void qc.invalidateQueries({ queryKey: ['tenants'] });
        void qc.invalidateQueries({ queryKey: ['white-labels'] });
        // `me` carries the tenants a session may enter; a new one is not reachable until it
        // refetches, so without this the operator creates a company and cannot open it.
        void qc.invalidateQueries({ queryKey: ['me'] });
      },
    }),
  };
}

/**
 * Like-for-like comparison inside one vehicle class.
 *
 * Disabled without a class rather than falling back to the whole fleet: an
 * unclassified vehicle has nothing comparable to be measured against, and a
 * fleet-wide median would be the confident wrong answer this feature exists to
 * avoid.
 */
export function useCohortComparison(vehicleClassId: string | null, vehicleId: string, days = 30) {
  const t = useTenantKey();
  return useQuery({
    // The vehicle is in the key: it decides the DRIVETRAIN half of the cohort,
    // so two vehicles in one class can have genuinely different answers.
    queryKey: ['cohort-comparison', t, vehicleClassId, vehicleId, days],
    queryFn: () => endpoints.cohortComparison({ vehicleClassId: vehicleClassId!, vehicleId, days }),
    enabled: !!vehicleClassId,
    staleTime: 15 * 60_000,
  });
}

/**
 * Fleet-wide CAN exceptions.
 *
 * One request for the whole fleet rather than one per vehicle: the server
 * aggregates 30 days across every vehicle in two grouped queries, and fanning
 * that out client-side would be hundreds of round trips to render one table.
 */
export function useFleetCanIntelligence(days = 30) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['fleet-can-intelligence', t, days],
    queryFn: () => endpoints.fleetCanIntelligence({ days }),
    staleTime: 15 * 60_000,
  });
}

/**
 * Evidence attachments for a recommendation's outcome.
 *
 * A 503 means the deployment has no object store configured — a real and
 * expected state, not a fault, so callers render an explanatory panel rather
 * than an error. It is not retried: retrying missing configuration only delays
 * the answer.
 */
export function useEvidence(recommendationId: string, enabled = true) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['evidence', t, recommendationId],
    queryFn: () => endpoints.evidenceList(recommendationId),
    enabled: enabled && !!recommendationId,
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 503) && count < 1,
  });
}

/**
 * The two-phase upload, as one mutation.
 *
 * 1. `initiate` — the server validates and returns a presigned PUT URL.
 * 2. PUT the bytes **straight to the object store**, never through the API.
 * 3. `complete` — the server verifies the stored object and marks it AVAILABLE.
 *
 * If step 3 fails after step 2 succeeded, the attachment is left PENDING and
 * the server may mark it QUARANTINED. That state is surfaced rather than
 * hidden: a silent PENDING row looks like a successful upload that produced
 * nothing.
 */
export function useUploadEvidence(recommendationId: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      evidenceType: EvidenceType;
      caption?: string;
    }): Promise<EvidenceAttachment> => {
      const checksumSha256 = await sha256Hex(input.file);
      const { attachment, upload } = await endpoints.initiateEvidence(recommendationId, {
        evidenceType: input.evidenceType,
        fileName: input.file.name,
        // Browsers leave `type` empty for unrecognised extensions; the API
        // requires a non-empty contentType and the allow-list rejects a blank.
        contentType: input.file.type || 'application/octet-stream',
        byteSize: input.file.size,
        checksumSha256,
        ...(input.caption ? { caption: input.caption } : {}),
      });
      await putToPresignedUrl(upload.url, input.file, upload.headers);
      return endpoints.completeEvidence(recommendationId, attachment.id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['evidence', t, recommendationId] });
      void qc.invalidateQueries({ queryKey: ['outcome-evidence-coverage', t] });
    },
  });
}

export function useDeleteEvidence(recommendationId: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (attachmentId: string) => endpoints.deleteEvidence(recommendationId, attachmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['evidence', t, recommendationId] });
      void qc.invalidateQueries({ queryKey: ['outcome-evidence-coverage', t] });
    },
  });
}

/**
 * Mints a short-TTL download URL on demand.
 *
 * Deliberately a mutation, not a query: each call is audited server-side and
 * yields a URL that expires, so it must not be cached, prefetched, or refetched
 * in the background.
 */
export function useEvidenceDownloadUrl(recommendationId: string) {
  return useMutation({
    mutationFn: (attachmentId: string) =>
      endpoints.evidenceDownloadUrl(recommendationId, attachmentId),
  });
}

/**
 * Outcome analytics.
 *
 * Each is tenant-namespaced and keyed by the selected period, so switching
 * period refetches rather than showing the previous window's figures under a
 * new label. `undefined` days means "all time" and is a distinct cache key from
 * any number — the endpoint rejects a non-positive `sinceDays`, so it must be
 * omitted rather than sent as 0.
 */
export function useOutcomeSummary(sinceDays?: number, asTenantId: string | null = null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-summary', asTenantId ?? t, sinceDays ?? 'all'],
    queryFn: () =>
      asTenantId
        ? endpoints.outcomeSummaryAs(asTenantId, sinceDays)
        : endpoints.outcomeSummary(sinceDays),
    retry: false,
  });
}

/**
 * Precision per policy version — which rules misfire, and whether tuning
 * one actually improved it.
 */
/**
 * What outcomes say about each policy version — V2.
 *
 * Keyed by the window like its neighbours, so changing the period refetches
 * rather than relabelling numbers computed over a different span.
 */
export function useOutcomePolicyLearning(sinceDays?: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-policy-learning', t, sinceDays ?? 'all'],
    queryFn: () => endpoints.outcomePolicyLearning(sinceDays),
  });
}

/**
 * Precision over time — whether tuning actually worked.
 *
 * Keyed by bucket as well as period: switching granularity must refetch rather
 * than relabel the previous shape.
 */
export function useOutcomeTrend(sinceDays?: number, bucket?: OutcomeTrendBucket) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-trend', t, sinceDays ?? 'all', bucket ?? 'default'],
    queryFn: () => endpoints.outcomeTrend(sinceDays, bucket),
  });
}

export function useOutcomeRootCauses(sinceDays?: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-root-causes', t, sinceDays ?? 'all'],
    queryFn: () => endpoints.outcomeRootCauses(sinceDays),
  });
}

export function useOutcomeConfidence(sinceDays?: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-confidence', t, sinceDays ?? 'all'],
    queryFn: () => endpoints.outcomeConfidence(sinceDays),
  });
}

export function useOutcomeTiming(sinceDays?: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-timing', t, sinceDays ?? 'all'],
    queryFn: () => endpoints.outcomeTiming(sinceDays),
  });
}

export function useOutcomePerformers(sinceDays?: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-performers', t, sinceDays ?? 'all'],
    queryFn: () => endpoints.outcomePerformers(sinceDays),
  });
}

export function useOutcomeBusinessImpact(sinceDays?: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-business-impact', t, sinceDays ?? 'all'],
    queryFn: () => endpoints.outcomeBusinessImpact(sinceDays),
  });
}

/** Not period-filtered — see the endpoint comment. */
export function useOutcomeEvidenceCoverage() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['outcome-evidence-coverage', t],
    queryFn: () => endpoints.outcomeEvidenceCoverage(),
  });
}

/**
 * The recorded outcome for a recommendation, or `null` if none exists.
 *
 * `GET :id/outcome` answers 404 when nothing has been recorded, which is the
 * normal state for most recommendations rather than a failure. That is mapped to
 * `null` here so callers can render "not yet recorded" without inspecting error
 * objects, and `retry` is disabled for it — retrying a 404 only delays the
 * answer, and the shared default is `retry: 1`.
 */
export function useRecommendationOutcome(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['recommendation-outcome', t, id],
    queryFn: async () => {
      try {
        return await endpoints.recommendationOutcome(id);
      } catch (e) {
        if (e instanceof ApiClientError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!id,
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 404) && count < 1,
  });
}

/**
 * Records or refines an outcome.
 *
 * The endpoint UPSERTS — at most one outcome exists per recommendation, and a
 * second call updates it in place rather than appending. Pessimistic, per
 * ADMIN-UI-CONVENTIONS: no optimistic update, invalidate after the server
 * confirms. The recommendation itself is invalidated too because recording
 * feedback can move its status.
 */
export function useRecordOutcome(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: RecordOutcomeRequest) => endpoints.recordRecommendationOutcome(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recommendation-outcome', t, id] });
      void qc.invalidateQueries({ queryKey: ['recommendation', t, id] });
    },
  });
}

export function useRegenerate(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: () => endpoints.regenerate(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['explanation-history', t, id] }),
  });
}

// ── vehicles ────────────────────────────────────────────────────────────────

/**
 * Correlation configuration — the settings that decide whether an incident
 * EXISTS and how events group onto it.
 *
 * Not tenant-key-namespaced by accident: it IS tenant data, so the key includes
 * the tenant like every other tenant-scoped query.
 */
export function useCorrelationConfig() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['correlation-config', t],
    queryFn: () => endpoints.correlationConfig(),
  });
}
export function useCorrelationConfigVersions() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['correlation-config-versions', t],
    queryFn: () => endpoints.correlationConfigVersions(),
  });
}
export function useCreateCorrelationConfigVersion() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CreateCorrelationConfigVersionRequest) =>
      endpoints.createCorrelationConfigVersion(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['correlation-config-versions', t] });
    },
  });
}
/**
 * Discard a version that never went live.
 *
 * Invalidates the same two keys the activation does. The VERSIONS list
 * obviously changes; `correlation-config` is invalidated too because the
 * "unsaved changes" diff on the page is computed against whatever is in force,
 * and a stale answer there would tell somebody they had edits they did not.
 */
/**
 * Replay the thresholds being edited over stored telemetry.
 *
 * A MUTATION hook for a read-only call, deliberately: it is expensive, it is
 * triggered by a button rather than by rendering, and a query would re-run it
 * on every focus. `useMutation` is the shape for "do this when I ask".
 */
export function useCorrelationConfigDryRun() {
  return useMutation({
    mutationFn: (body: DryRunRequest) => endpoints.correlationConfigDryRun(body),
  });
}

export function useDiscardCorrelationConfigVersion() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (id: string) => endpoints.discardCorrelationConfigVersion(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['correlation-config', t] });
      void qc.invalidateQueries({ queryKey: ['correlation-config-versions', t] });
    },
  });
}

export function useActivateCorrelationConfigVersion() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (id: string) => endpoints.activateCorrelationConfigVersion(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['correlation-config', t] });
      void qc.invalidateQueries({ queryKey: ['correlation-config-versions', t] });
    },
  });
}

/**
 * Drivers. Key shapes mirror the vehicle hooks exactly — ['drivers', tenant, q]
 * and ['driver', tenant, id] — because the mutation hooks below invalidate on
 * those prefixes, and a mismatch invalidates nothing while the list silently
 * keeps showing stale rows.
 */
export function useDrivers(q: ListParams<DriverListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.drivers(q), ['drivers', t, q]);
}
export function useDriver(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['driver', t, id],
    queryFn: () => endpoints.driver(id),
    enabled: !!id,
  });
}
export function useCreateDriver() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CreateDriverRequest) => endpoints.createDriver(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['drivers', t] }),
  });
}
export function useUpdateDriver(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: UpdateDriverRequest) => endpoints.updateDriver(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['driver', t, id] });
      void qc.invalidateQueries({ queryKey: ['drivers', t] });
    },
  });
}
/** Archive and restore share a hook, as the vehicle lifecycle does. */
export function useDriverLifecycle(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (v: { action: 'archive' | 'restore'; note?: string }) =>
      v.action === 'archive' ? endpoints.archiveDriver(id, v.note) : endpoints.restoreDriver(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['driver', t, id] });
      void qc.invalidateQueries({ queryKey: ['drivers', t] });
    },
  });
}

export function useVehicles(q: ListParams<VehicleListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.vehicles(q), ['vehicles', t, q]);
}
export function useVehicle(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['vehicle', t, id],
    queryFn: () => endpoints.vehicle(id),
    enabled: !!id,
  });
}
export function useVehicleEvents(id: string, q: ListParams<VehicleEventListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.vehicleEvents(id, q), ['vehicle-events', t, id, q]);
}
export function useVehicleIncidents(id: string, q: ListParams<IncidentListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.vehicleIncidents(id, q), ['vehicle-incidents', t, id, q]);
}
export function useVehicleRecommendations(id: string, q: ListParams<RecommendationListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.vehicleRecommendations(id, q), ['vehicle-recs', t, id, q]);
}

// ── policies ────────────────────────────────────────────────────────────────

export function usePolicies(q: ListParams<PolicyListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.policies(q), ['policies', t, q]);
}
export function usePolicy(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['policy', t, id],
    queryFn: () => endpoints.policy(id),
    enabled: !!id,
  });
}
export function useCreatePolicy() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CreatePolicyRequest) => endpoints.createPolicy(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['policies', t] }),
  });
}
export function useCreatePolicyVersion(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CreatePolicyVersionRequest) => endpoints.createPolicyVersion(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['policy', t, id] });
      // The LIST carries the version count, so it goes stale too.
      void qc.invalidateQueries({ queryKey: ['policies', t] });
    },
  });
}
/**
 * What a draft version would have done to evaluations that already happened.
 *
 * A mutation rather than a query because it computes on demand and should not
 * run just because a page rendered — but it writes nothing, so nothing is
 * invalidated on success.
 */
export function useBacktestPolicyVersion(id: string) {
  return useMutation({
    mutationFn: (vars: { versionId: string; days?: number }) =>
      endpoints.backtestPolicyVersion(id, vars.versionId, vars.days ?? 30),
  });
}

/**
 * Deactivate, reactivate, archive or restore a policy.
 *
 * Every one of these changes a column the LIST renders — status, and whether
 * the row appears at all — so the list is what gets invalidated. The detail goes
 * too, because a page open on the policy shows the same status chip.
 */
export function usePolicyLifecycle(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: PolicyLifecycleRequest) => endpoints.policyLifecycle(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['policies', t] });
      void qc.invalidateQueries({ queryKey: ['policy', t, id] });
    },
  });
}

/**
 * Delete a policy that has never influenced anything.
 *
 * The detail query is REMOVED rather than invalidated: refetching a row that no
 * longer exists turns a successful deletion into a 404 on screen.
 */
export function useDeletePolicy() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (id: string) => endpoints.deletePolicy(id),
    onSuccess: (_r, id) => {
      qc.removeQueries({ queryKey: ['policy', t, id] });
      void qc.invalidateQueries({ queryKey: ['policies', t] });
    },
  });
}

/**
 * Discard a draft version that never governed anything.
 *
 * Both the detail and the list go stale: the detail holds the version rows, and
 * the list carries the version COUNT.
 */
/**
 * Edit a draft version in place.
 *
 * The list carries no version content, but it does carry the version COUNT and
 * the active version, neither of which an edit changes — so only the detail is
 * invalidated. Deliberately not optimistic: the server re-checks that the draft
 * is still a draft, and a draft can be activated while the form is open.
 */
export function useUpdatePolicyVersion(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (vars: { versionId: string; definition: PolicyDefinitionInput }) =>
      endpoints.updatePolicyVersion(id, vars.versionId, { definition: vars.definition }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['policy', t, id] }),
  });
}

export function useDiscardPolicyVersion(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (versionId: string) => endpoints.discardPolicyVersion(id, versionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['policy', t, id] });
      void qc.invalidateQueries({ queryKey: ['policies', t] });
    },
  });
}

export function useActivatePolicyVersion(id: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (versionId: string) => endpoints.activatePolicyVersion(id, versionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['policy', t, id] });
      /*
       * The LIST too, and this one is not optional.
       *
       * Activating a version also flips the POLICY from DRAFT to ACTIVE, in the
       * same transaction — and status, version count and active version are all
       * columns of the list, not the detail. Invalidating only the detail left
       * the row still reading DRAFT until the page was reloaded by hand: the
       * server had done the right thing and the screen disagreed, which is
       * indistinguishable from the activation having failed.
       */
      void qc.invalidateQueries({ queryKey: ['policies', t] });
    },
  });
}

// ── audit ─────────────────────────────────────────────────────────────────

export function useAudit(q: ListParams<AuditListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.audit(q), ['audit', t, q]);
}

// ── admin ─────────────────────────────────────────────────────────────────

export function useUsers(q: ListParams<AdminMemberListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.users(q), ['users', t, q]);
}
export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => endpoints.roles() });
}
export function usePermissions() {
  return useQuery({ queryKey: ['permissions'], queryFn: () => endpoints.permissions() });
}
export function useProviderConnections() {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['provider-connections', t],
    queryFn: () => endpoints.providerConnections(),
  });
}
/**
 * External report recipients (D23).
 *
 * Every one of these invalidates the reports query rather than patching a local
 * copy — the server decides status from `verifiedAt` and `expiresAt`, and a
 * client that computed it would be a second opinion on a data-egress control.
 */
export function useAddExternalRecipient(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddExternalRecipient) => endpoints.addExternalRecipient(reportId, body),
    /*
     * The reports query is keyed `['scheduled-reports']` with no tenant segment.
     * Invalidation is PREFIX matching, so a longer key would match nothing and
     * the panel would silently show stale rows after every change.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  });
}

export function useVerifyExternalRecipient(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { recipientId: string; code: string }) =>
      endpoints.verifyExternalRecipient(reportId, v.recipientId, v.code),
    /*
     * The reports query is keyed `['scheduled-reports']` with no tenant segment.
     * Invalidation is PREFIX matching, so a longer key would match nothing and
     * the panel would silently show stale rows after every change.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  });
}

export function useResendExternalRecipientCode(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipientId: string) =>
      endpoints.resendExternalRecipientCode(reportId, recipientId),
    /*
     * The reports query is keyed `['scheduled-reports']` with no tenant segment.
     * Invalidation is PREFIX matching, so a longer key would match nothing and
     * the panel would silently show stale rows after every change.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  });
}

export function useRemoveExternalRecipient(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipientId: string) => endpoints.removeExternalRecipient(reportId, recipientId),
    /*
     * The reports query is keyed `['scheduled-reports']` with no tenant segment.
     * Invalidation is PREFIX matching, so a longer key would match nothing and
     * the panel would silently show stale rows after every change.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  });
}

/**
 * Everyone with a live session.
 *
 * Refetched on focus, unlike most lists here: somebody reading this during an
 * incident wants what is true now, and a stale answer to "who is connected" is
 * worse than a slow one.
 */
export function useActiveSessions() {
  return useQuery({
    queryKey: ['active-sessions'],
    queryFn: () => endpoints.activeSessions(),
    refetchOnWindowFocus: true,
  });
}

/**
 * A user's login sessions. Platform-scoped, so NOT keyed by tenant — a session
 * belongs to a global identity rather than to an organisation.
 */
export function useLoginSessions(userId: string) {
  return useQuery({
    queryKey: ['login-sessions', userId],
    queryFn: () => endpoints.loginSessions(userId),
    enabled: !!userId,
  });
}

export function useRevokeLoginSession(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (familyId: string) => endpoints.revokeLoginSession(userId, familyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['login-sessions', userId] }),
  });
}

export function useRevokeAllLoginSessions(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => endpoints.revokeAllLoginSessions(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['login-sessions', userId] }),
  });
}

/** Store a credential the tenant supplied. Write-only: nothing reads it back. */
export function useStoreProviderCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: StoreProviderCredential }) =>
      endpoints.storeProviderCredential(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provider-connections'] }),
  });
}

/**
 * Pull zone definitions now.
 *
 * Invalidates both the connections (for the new sync timestamps) and the
 * geofences (for the zones themselves) — a button that says it synced while the
 * geofence list still shows yesterday's zones is the kind of half-truth this
 * codebase keeps finding.
 */
export function useSyncProviderGeofences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpoints.syncProviderGeofences(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['provider-connections'] });
      void qc.invalidateQueries({ queryKey: ['geofences'] });
    },
  });
}

/** Configure outbound calls to a provider. */
export function useUpdateProviderOutbound() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: UpdateProviderOutbound }) =>
      endpoints.updateProviderOutbound(v.id, v.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provider-connections'] }),
  });
}

/** What became of this tenant's events. Tenant-scoped, so keyed by tenant. */
export function useEventProcessingHealth(asTenantId: string | null = null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['event-processing-health', asTenantId ?? t],
    queryFn: () =>
      asTenantId
        ? endpoints.eventProcessingHealthAs(asTenantId)
        : endpoints.eventProcessingHealth(),
    // A refusal is an answer here — the card omits itself rather than retrying
    // a 403 three times on every dashboard load.
    retry: false,
  });
}
/**
 * The raw payloads this tenant's provider sent.
 *
 * Keyed by tenant AND by every filter, because the audit question is always a
 * narrow one — "this provider event id", "this window" — and a cache that
 * ignored the filters would answer the previous question.
 */
/**
 * Mint a connection and its ingestion key.
 *
 * Invalidates the connections list, because the new row must appear — and the
 * KEY is deliberately not cached anywhere: it is returned once, held in
 * component state until acknowledged, and then gone.
 */
export function useCreateProviderConnection() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CreateProviderConnectionRequest) => endpoints.createProviderConnection(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provider-connections', t] }),
  });
}

export function useRotateProviderConnectionKey() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (id: string) => endpoints.rotateProviderConnectionKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provider-connections', t] }),
  });
}

export function useInboundEvents(q: InboundEventQuery) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['inbound-events', t, q],
    queryFn: () => endpoints.inboundEvents(q),
  });
}

/** One payload, fetched only when a row is opened — it can be large. */
export function useInboundEvent(id: string | null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['inbound-event', t, id],
    queryFn: () => endpoints.inboundEvent(id!),
    enabled: !!id,
  });
}

export function useTenantSettings() {
  const t = useTenantKey();
  return useQuery({ queryKey: ['tenant-settings', t], queryFn: () => endpoints.tenantSettings() });
}
export function useLlmSettings() {
  const t = useTenantKey();
  return useQuery({ queryKey: ['llm-settings', t], queryFn: () => endpoints.llmSettings() });
}

// ── llm ─────────────────────────────────────────────────────────────────────

/**
 * The prompt registry.
 *
 * A PLATFORM administrator holds no tenant membership, so `TenantContextGuard`
 * refuses them the tenant-scoped route — which would leave the only person
 * permitted to author a prompt unable to read one. They read the same payload
 * from the platform route instead.
 */
export function usePrompts() {
  const { user } = useAuth();
  const platform = !!user?.isPlatformAdmin;
  return useQuery({
    queryKey: ['prompts', platform ? 'platform' : 'tenant'],
    queryFn: () => (platform ? endpoints.platformPrompts() : endpoints.prompts()),
  });
}

/*
 * Prompt authoring. Not tenant-keyed, unlike almost everything else here:
 * `LlmPrompt` has no tenant, so there is one cache entry and a change is a
 * change for everybody.
 */
function usePromptMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['prompts'] }),
  });
}

export function useCreatePrompt() {
  return usePromptMutation((body: CreatePromptRequest) => endpoints.createPrompt(body));
}

export function useDeletePrompt() {
  return usePromptMutation((promptId: string) => endpoints.deletePrompt(promptId));
}

/**
 * Point this tenant at a prompt key, or back at the platform default.
 *
 * Invalidates the LLM settings rather than the prompts: the prompt did not
 * change, the tenant's choice of one did.
 */
export function useSetPromptKey() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: PromptKeyOverrideRequest) => endpoints.setPromptKey(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['llm-settings', t] }),
  });
}

export function useCreatePromptVersion(promptId: string) {
  return usePromptMutation((body: CreatePromptVersionRequest) =>
    endpoints.createPromptVersion(promptId, body),
  );
}

export function useUpdatePromptVersion(promptId: string) {
  return usePromptMutation((vars: { versionId: string; body: UpdatePromptVersionRequest }) =>
    endpoints.updatePromptVersion(promptId, vars.versionId, vars.body),
  );
}

export function useActivatePromptVersion(promptId: string) {
  return usePromptMutation((versionId: string) =>
    endpoints.activatePromptVersion(promptId, versionId),
  );
}

export function useDiscardPromptVersion(promptId: string) {
  return usePromptMutation((versionId: string) =>
    endpoints.discardPromptVersion(promptId, versionId),
  );
}
export function useExecutions(q: ListParams<LlmExecutionListQuery>) {
  const t = useTenantKey();
  return useListQuery(() => endpoints.executions(q), ['executions', t, q]);
}
export function useExecution(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['execution', t, id],
    queryFn: () => endpoints.execution(id),
    enabled: !!id,
  });
}
export function useExplanationHistory(id: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['explanation-history', t, id],
    queryFn: () => endpoints.explanationHistory(id),
    enabled: !!id,
  });
}

// ── platform: tenants ───────────────────────────────────────────────────────
//
// Platform queries are NOT namespaced by tenant: they are tenant-independent by
// definition, and keying them by the active tenant would evict them pointlessly
// whenever the user switched tenants on the way back to a tenant route.

export function usePlatformTenants(q: ListParams<TenantListQuery>) {
  // `keepPreviousData` via useListQuery: paging must not blank the table.
  return useListQuery(() => endpoints.platformTenants(q), platformKeys.tenants.list(q));
}

export function usePlatformTenant(id: string) {
  return useQuery({
    queryKey: platformKeys.tenants.detail(id),
    queryFn: () => endpoints.platformTenant(id),
    enabled: !!id,
  });
}

export function useCreatePlatformTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantRequest) => endpoints.createPlatformTenant(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: platformKeys.tenants.all }),
  });
}

export function useUpdatePlatformTenant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTenantRequest) => endpoints.updatePlatformTenant(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.tenants.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.tenants.lists() });
    },
  });
}

/**
 * Suspend/reactivate. Pessimistic by design: the server owns the transition and
 * rejects a repeat with 409, so an optimistic flip would show success and then
 * reverse itself.
 */
export function useTenantLifecycle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { action: 'suspend' | 'reactivate'; note?: string }) =>
      input.action === 'suspend'
        ? endpoints.suspendPlatformTenant(id, input.note)
        : endpoints.reactivatePlatformTenant(id, input.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.tenants.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.tenants.lists() });
    },
  });
}

// ── vehicles: mutations ─────────────────────────────────────────────────────
//
// Tenant-scoped, so invalidation uses the tenant-namespaced keys the read
// hooks already use — switching tenant must not surface another tenant's
// cached fleet.

export function useCreateVehicle() {
  const qc = useQueryClient();
  const tenantKey = useTenantKey();
  return useMutation({
    mutationFn: (body: CreateVehicleRequest) => endpoints.createVehicle(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['vehicles', tenantKey] }),
  });
}

export function useUpdateVehicle(id: string) {
  const qc = useQueryClient();
  const tenantKey = useTenantKey();
  return useMutation({
    mutationFn: (body: UpdateVehicleRequest) => endpoints.updateVehicle(id, body),
    onSuccess: () => {
      // Key shape must match the read hooks exactly — ['vehicle', tenant, id]
      // and ['vehicles', tenant, query]. A mismatched prefix invalidates
      // nothing, and the list silently keeps showing stale data.
      void qc.invalidateQueries({ queryKey: ['vehicle', tenantKey, id] });
      void qc.invalidateQueries({ queryKey: ['vehicles', tenantKey] });
    },
  });
}

/**
 * Archive and restore. Pessimistic: the server owns the transition, and
 * archiving keeps the registration reserved (behaviour decision D3), so an
 * optimistic flip could mislead about whether the plate is free.
 */
export function useVehicleLifecycle(id: string) {
  const qc = useQueryClient();
  const tenantKey = useTenantKey();
  return useMutation({
    mutationFn: (input: { action: 'archive' | 'restore'; note?: string }) =>
      input.action === 'archive'
        ? endpoints.archiveVehicle(id, input.note)
        : endpoints.restoreVehicle(id),
    onSuccess: () => {
      // Key shape must match the read hooks exactly — ['vehicle', tenant, id]
      // and ['vehicles', tenant, query]. A mismatched prefix invalidates
      // nothing, and the list silently keeps showing stale data.
      void qc.invalidateQueries({ queryKey: ['vehicle', tenantKey, id] });
      void qc.invalidateQueries({ queryKey: ['vehicles', tenantKey] });
    },
  });
}

// ── the partner console ─────────────────────────────────────────────────────
//
// Partner-scoped, so deliberately NOT tenant-keyed: a partner administrator has
// no tenant context until they enter one of their companies, and keying these
// by the active tenant would cache the partner's own companies under whichever
// customer they last visited.

export function usePartnerContext() {
  return useQuery({
    queryKey: platformKeys.partner.context(),
    queryFn: () => endpoints.partnerContext(),
  });
}

export function usePartnerCompanies() {
  return useQuery({
    queryKey: platformKeys.partner.companies(),
    queryFn: () => endpoints.partnerCompanies(),
  });
}

/**
 * A reseller's vehicles, fetched only when its branch is expanded.
 *
 * LAZY on purpose. Loading every vehicle of every reseller to render a
 * collapsed tree would fetch thousands of rows a partner has not asked to see,
 * and the counts already on each node answer "how many" without any of them.
 */
export function usePartnerFleetAccountVehicles(tenantId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['partner', 'reseller-vehicles', tenantId],
    queryFn: () => endpoints.partnerFleetAccountVehicles(tenantId),
    enabled: enabled && !!tenantId,
    // A fleet does not change while somebody reads a tree.
    staleTime: 60_000,
  });
}

/**
 * A partner's RESELLERS — the commercial level between it and its companies.
 *
 * ⚠️ Not `usePartnerFleetAccountVehicles` above, which reads a FLEET ACCOUNT. Those two
 * words named one thing until the vocabulary was corrected; they are two levels apart.
 */
export function usePartnerResellers(enabled = true) {
  return useQuery({
    queryKey: platformKeys.partner.resellers(),
    queryFn: () => endpoints.partnerResellers(),
    /*
     * ⚠️ Partner-only on the server, so a RESELLER fetching it earns a 403 on every load
     * of its own console. Defaulted true, so every existing caller is unchanged.
     */
    enabled,
  });
}

/**
 * The policy templates a new company can start from.
 *
 * ⚠️ Fetched only while the dialog is OPEN. `CreateCompanyDialog` was always mounted once
 * before, so a partner-only query ran on every load of the companies page — a 403 per load
 * for a reseller. Mounting IS the fetch.
 */
export function usePolicyTemplates(enabled = true) {
  return useQuery({
    queryKey: platformKeys.partner.policyTemplates(),
    queryFn: () => endpoints.policyTemplates(),
    enabled,
  });
}

/**
 * ⚠️ Every reseller mutation invalidates the COMPANIES list too.
 *
 * A move changes which reseller a company names, and a withdrawal can change nothing else
 * — but a rename changes the `resellerName` every company row is showing. Invalidating
 * only the reseller list would leave the companies table naming a reseller that no longer
 * exists under that name, which reads as the save not having worked.
 */
function useResellerMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.partner.resellers() });
      void qc.invalidateQueries({ queryKey: platformKeys.partner.companies() });
    },
  });
}

export function useCreatePartnerReseller() {
  return useResellerMutation((body: CreatePartnerResellerRequest) =>
    endpoints.createPartnerReseller(body),
  );
}

export function useRenamePartnerReseller() {
  return useResellerMutation((v: { id: string; name: string }) =>
    endpoints.renamePartnerReseller(v.id, { name: v.name }),
  );
}

export function useSetPartnerResellerStatus() {
  return useResellerMutation((v: { id: string; status: 'ACTIVE' | 'SUSPENDED' }) =>
    endpoints.setPartnerResellerStatus(v.id, { status: v.status }),
  );
}

export function useWithdrawPartnerReseller() {
  return useResellerMutation((id: string) => endpoints.withdrawPartnerReseller(id));
}

/**
 * What one reseller may sell.
 *
 * ⚠️ `enabled` on the id, so a CLOSED dialog fetches nothing — the rule
 * `PartnerHierarchyTree` established and the one that silently stops being true. The
 * ceiling is read per reseller and there may be many.
 */
export function usePartnerResellerFeatures(resellerId: string | null) {
  return useQuery({
    queryKey: platformKeys.partner.resellerFeatures(resellerId ?? ''),
    queryFn: () => endpoints.partnerResellerFeatures(resellerId!),
    enabled: resellerId !== null,
  });
}

/**
 * ⚠️ Invalidates the RESELLER LIST as well as the ceiling.
 *
 * A row shows whether a reseller is restricted, so changing the ceiling changes the list —
 * invalidating only the dialog's own query would leave the row behind it saying
 * "unrestricted" while the dialog beside it shows an allow-list. The `resellerName` lesson
 * one mutation up.
 */
function useResellerFeatureMutation<TArgs>(
  resellerId: string,
  fn: (args: TArgs) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: platformKeys.partner.resellerFeatures(resellerId),
      });
      void qc.invalidateQueries({ queryKey: platformKeys.partner.resellers() });
    },
  });
}

export function useSetPartnerResellerFeature(resellerId: string) {
  return useResellerFeatureMutation(resellerId, (v: SetPartnerResellerFeatureRequest) =>
    endpoints.setPartnerResellerFeature(resellerId, v),
  );
}

export function useClearPartnerResellerRestriction(resellerId: string) {
  return useResellerFeatureMutation(resellerId, () =>
    endpoints.clearPartnerResellerRestriction(resellerId),
  );
}

export function useMoveCompanyReseller() {
  return useResellerMutation((v: { tenantId: string; resellerId: string }) =>
    endpoints.moveCompanyReseller(v.tenantId, { resellerId: v.resellerId }),
  );
}

/**
 * The PLATFORM's view of one partner's resellers.
 *
 * ⚠️ A separate hook rather than a parameter on `usePartnerResellers`, because the two read
 * different ROUTES under different guards — the partner's own console resolves its partner
 * from the session, this one names it. They share the repository on the server, which is
 * where sharing matters.
 */
export function usePlatformPartnerResellers(id: string) {
  return useQuery({
    queryKey: platformKeys.whiteLabels.resellers(id),
    queryFn: () => endpoints.platformPartnerResellers(id),
    enabled: !!id,
  });
}

function usePlatformResellerMutation<TArgs>(id: string, fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.resellers(id) });
      // A rename changes the reseller NAME every company row shows, so the companies
      // list is stale too — leaving it would show a reseller that no longer exists
      // under that name, which reads as the save not having worked.
      void qc.invalidateQueries({
        queryKey: [...platformKeys.whiteLabels.detail(id), 'companies'],
      });
    },
  });
}

export function useCreatePlatformPartnerReseller(id: string) {
  return usePlatformResellerMutation(id, (body: CreatePartnerResellerRequest) =>
    endpoints.createPlatformPartnerReseller(id, body),
  );
}

export function useRenamePlatformPartnerReseller(id: string) {
  return usePlatformResellerMutation(id, (v: { id: string; name: string }) =>
    endpoints.renamePlatformPartnerReseller(id, v.id, { name: v.name }),
  );
}

export function useWithdrawPlatformPartnerReseller(id: string) {
  return usePlatformResellerMutation(id, (resellerId: string) =>
    endpoints.withdrawPlatformPartnerReseller(id, resellerId),
  );
}

export function usePartnerMembers() {
  return useQuery({
    queryKey: platformKeys.partner.members(),
    queryFn: () => endpoints.partnerMembers(),
  });
}

export function useAddPartnerMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddPartnerMemberRequest) => endpoints.addPartnerMember(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: platformKeys.partner.members() }),
  });
}

export function useSetPartnerMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; body: UpdatePartnerMemberRequest }) =>
      endpoints.setPartnerMember(input.userId, input.body),
    /*
     * The response IS the whole list, so it is written straight into the cache
     * rather than refetched. `adminCount` decides what the screen offers, and a
     * moment where the list is stale is a moment where it offers a removal the
     * server would refuse.
     */
    onSuccess: (data) => qc.setQueryData(platformKeys.partner.members(), data),
  });
}

// The platform's view of a partner's people — the way back in when a partner
// has none of its own.

export function usePlatformPartnerMembers(id: string) {
  return useQuery({
    queryKey: platformKeys.whiteLabels.members(id),
    queryFn: () => endpoints.platformPartnerMembers(id),
    enabled: !!id,
  });
}

export function useAddPlatformPartnerMember(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddPartnerMemberRequest) => endpoints.addPlatformPartnerMember(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.members(id) });
      // …and the partner row itself, whose `adminCount` is what warns that a
      // partner is unreachable. Leaving it stale keeps the warning up beside
      // the administrator that just answered it.
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.lists() });
    },
  });
}

export function useSetPlatformPartnerMember(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; body: UpdatePartnerMemberRequest }) =>
      endpoints.setPlatformPartnerMember(id, input.userId, input.body),
    onSuccess: (data) => {
      qc.setQueryData(platformKeys.whiteLabels.members(id), data);
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.lists() });
    },
  });
}

export function usePartnerBranding() {
  return useQuery({
    queryKey: platformKeys.partner.branding(),
    queryFn: () => endpoints.partnerBranding(),
  });
}

export function useUpdatePartnerBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePartnerBranding) => endpoints.updatePartnerBranding(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.partner.branding() });
      /*
       * And the themes, which is what the partner just changed: their brand
       * resolves down into every company and reseller beneath them, so a cache
       * keyed only on the partner would leave somebody looking at the old logo
       * on the very page they set the new one from.
       *
       * Both keys, spelled as `TenantThemeProvider` spells them. The first
       * attempt used `['theme']`, which matches neither and would have been a
       * call that silently did nothing.
       */
      void qc.invalidateQueries({ queryKey: ['tenant-theme'] });
      void qc.invalidateQueries({ queryKey: ['public-theme'] });
    },
  });
}

export function useCreatePartnerCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartnerCompanyRequest) => endpoints.createPartnerCompany(body),
    /*
     * Only the partner's own queries. `me` — which is what the switcher reads,
     * and what has to learn about the new company — is NOT a TanStack query: it
     * is state in `AuthProvider` with an explicit `refreshUser`. Invalidating a
     * key nothing registered is a call that silently does nothing, so the
     * caller refreshes the session itself.
     */
    onSuccess: () => void qc.invalidateQueries({ queryKey: platformKeys.partner.all }),
  });
}

// ── platform: white-label partners ──────────────────────────────────────────
//
// The tier above tenants. Platform-scoped throughout, so these use the
// platform-namespaced keys rather than the tenant-namespaced ones: a partner
// has no tenant, and keying by the active tenant would cache one partner's list
// under whichever customer the administrator last entered.

export function usePlatformWhiteLabels(q: ListParams<WhiteLabelListQuery>) {
  return useListQuery(() => endpoints.platformWhiteLabels(q), platformKeys.whiteLabels.list(q));
}

export function usePlatformWhiteLabel(id: string) {
  return useQuery({
    queryKey: platformKeys.whiteLabels.detail(id),
    queryFn: () => endpoints.platformWhiteLabel(id),
    enabled: !!id,
  });
}

export function usePlatformWhiteLabelCompanies(id: string) {
  return useQuery({
    queryKey: [...platformKeys.whiteLabels.detail(id), 'companies'],
    queryFn: () => endpoints.platformWhiteLabelCompanies(id),
    enabled: !!id,
  });
}

export function useCreatePlatformWhiteLabel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWhiteLabelRequestInput) => endpoints.createPlatformWhiteLabel(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.all }),
  });
}

export function useUpdatePlatformWhiteLabel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateWhiteLabelRequest) => endpoints.updatePlatformWhiteLabel(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.lists() });
    },
  });
}

export function usePlatformWhiteLabelFeatures(id: string) {
  return useQuery({
    queryKey: platformKeys.whiteLabels.features(id),
    queryFn: () => endpoints.platformWhiteLabelFeatures(id),
    enabled: !!id,
  });
}

/**
 * Pessimistic, like every governance action: the server owns the ceiling and
 * refuses what the client cannot evaluate, so an optimistic flip would show a
 * grant that then reversed itself.
 *
 * Invalidates the TENANT feature caches too. Lowering a ceiling changes what
 * every company beneath it effectively holds without touching a single tenant
 * row, so a cache keyed only on partners would keep showing a feature as
 * granted on the very screen that reports it as ABOVE_CEILING.
 */
export function useSetPlatformWhiteLabelFeature(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateWhiteLabelFeature) => endpoints.setPlatformWhiteLabelFeature(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.whiteLabels.features(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.tenants.all });
    },
  });
}

// ── platform: users ─────────────────────────────────────────────────────────

export function usePlatformUsers(q: ListParams<PlatformUserListQuery>) {
  return useListQuery(() => endpoints.platformUsers(q), platformKeys.users.list(q));
}

export function usePlatformUser(id: string) {
  return useQuery({
    queryKey: platformKeys.users.detail(id),
    queryFn: () => endpoints.platformUser(id),
    enabled: !!id,
  });
}

export function useCreatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePlatformUserRequest) => endpoints.createPlatformUser(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: platformKeys.users.all }),
  });
}

export function useUpdatePlatformUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePlatformUserRequest) => endpoints.updatePlatformUser(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.users.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.users.lists() });
    },
  });
}

/**
 * Grant or revoke the platform capability.
 *
 * Pessimistic and never retried: the server refuses self-modification (403) and
 * removal of the last authenticable platform administrator (409), and neither
 * is something the client can evaluate in advance.
 */
export function usePlatformAdminCapability(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { action: 'grant' | 'revoke'; note?: string }) =>
      input.action === 'grant'
        ? endpoints.grantPlatformAdmin(id, input.note)
        : endpoints.revokePlatformAdmin(id, input.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: platformKeys.users.detail(id) });
      void qc.invalidateQueries({ queryKey: platformKeys.users.lists() });
    },
  });
}

// ── tenant members ──────────────────────────────────────────────────────────
//
// Tenant-scoped, so invalidation uses the tenant-namespaced key shape the
// existing read hook uses: ['users', tenant, query].

export function useMember(userId: string) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['member', t, userId],
    queryFn: () => endpoints.member(userId),
    enabled: !!userId,
  });
}

export function useAddMember() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CreateMembershipRequest) => endpoints.addMember(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users', t] }),
  });
}

export function useUpdateMemberRole(userId: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: UpdateMembershipRequest) => endpoints.updateMemberRole(userId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['member', t, userId] });
      void qc.invalidateQueries({ queryKey: ['users', t] });
    },
  });
}

/**
 * Enable/disable a membership.
 *
 * Pessimistic: the server refuses self-modification (403) and removal of the
 * last administrator who can actually authenticate (409). Neither is something
 * the client can evaluate — the quorum counts global account state the tenant
 * API does not expose.
 */
export function useMemberLifecycle(userId: string) {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (action: 'disable' | 'enable') =>
      action === 'disable' ? endpoints.disableMember(userId) : endpoints.enableMember(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['member', t, userId] });
      void qc.invalidateQueries({ queryKey: ['users', t] });
    },
  });
}

// ── platform: audit ─────────────────────────────────────────────────────────

export function usePlatformAudit(q: ListParams<PlatformAuditListQuery>) {
  return useListQuery(() => endpoints.platformAudit(q), platformKeys.audit.list(q));
}

/**
 * Telemetry pipeline health.
 *
 * Polled rather than fetched once: the figures move on their own — a rollup
 * catching up, a queue draining — and an operations page whose numbers are
 * from whenever it was opened invites the wrong conclusion. Sixty seconds is
 * well inside the slowest thing shown (the rollup runs every fifteen minutes)
 * and far cheaper than the alternative of people reloading by hand.
 */
/**
 * The operations dashboard's figures for one window.
 *
 * `placeholderData` keeps the previous period's numbers on screen while a new
 * window loads, so changing the selector does not blank the page and bounce the
 * layout — the same reason the list pages keep their previous page.
 */
/**
 * Findings already taken, most recent first.
 *
 * Tenant-keyed like every other tenant-scoped query: a finding names one
 * branch's vehicles, so switching tenant must refetch rather than leave the
 * previous organisation's answers on screen under a new name.
 */
export function useCopilotFindings(investigationId: string | null, includeDismissed = false) {
  const t = useTenantKey();
  return useQuery({
    // Keyed by investigation AND by whether cleared findings are being shown:
    // the two are different lists, and sharing a key would serve one under the
    // other's control.
    queryKey: ['copilot', 'findings', t, investigationId ?? 'none', includeDismissed],
    queryFn: () => endpoints.copilotFindings(investigationId ?? undefined, includeDismissed),
    /*
     * No investigation, no findings — the query does not run.
     *
     * The endpoint answers an unscoped request with every finding the tenant
     * holds, which is right for the API and wrong for this screen. Passing
     * `undefined` while the list was empty or still loading put every answer
     * ever taken under a header that named none of them, which is the same
     * disagreement between header and list that scoping the query was meant to
     * end — surviving in the one case nobody looks at.
     */
    enabled: investigationId !== null,
    /*
     * Polls only while wording is actually in flight, and stops the moment
     * nothing is PENDING. Wording arrives from a worker, so there is no response
     * to wait on — and a fixed interval would keep polling a screen where every
     * finding settled hours ago.
     */
    refetchInterval: (query) =>
      query.state.data?.items.some((f) => f.wordingStatus === 'PENDING') ? 3_000 : false,
  });
}

/**
 * Run a capability.
 *
 * Not optimistic, and never will be. The server applies guards the client
 * cannot evaluate — the entitlement, the role, and whether the subject exists in
 * this branch — and a finding that appeared and then vanished would be worse
 * than one that took a moment to arrive.
 */
export function useCopilotInvestigations(includeArchived = false) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['copilot', 'investigations', t, includeArchived],
    queryFn: () => endpoints.copilotInvestigations(includeArchived),
  });
}

export function useCopilotInvestigationUpdate() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (i: { id: string; name?: string; status?: string }) =>
      endpoints.updateCopilotInvestigation(i.id, { name: i.name, status: i.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['copilot', 'investigations', t] });
      void qc.invalidateQueries({ queryKey: ['copilot', 'findings', t] });
    },
  });
}

/**
 * Pin or unpin, pessimistically.
 *
 * No optimistic update: pinning reorders the list, and a row that jumped and
 * then jumped back would be worse than one that took a moment to move.
 */
export function useCopilotUsage(days = 30, enabled = true) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['copilot', 'usage', t, days],
    queryFn: () => endpoints.copilotUsage(days),
    enabled,
  });
}

/**
 * Record that a typed question resolved to nothing.
 *
 * ⚠️ Fire-and-forget, and it must never block or fail the reader's flow: this
 * is telemetry about the PRODUCT, not about their question, and a refusal that
 * also showed an error would punish somebody for asking.
 */
export function useCopilotUnresolved() {
  return useMutation({ mutationFn: () => endpoints.copilotUnresolved() });
}

/**
 * Server-side routing, for a question the browser matcher could not place.
 *
 * ⚠️ **Not fire-and-forget, unlike `useCopilotUnresolved`.** The reader is
 * waiting on this one — it decides what gets asked — so its pending state has to
 * reach the composer, or somebody presses Ask again while a round-trip is
 * already running.
 */
export function useCopilotRoute() {
  return useMutation({
    mutationFn: (v: { question: string; skipMatch?: boolean; windowHours?: number }) =>
      endpoints.copilotRoute(v.question, v.skipMatch ?? false, v.windowHours),
  });
}

export function useCopilotDismiss() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (i: { id: string; dismissed: boolean }) =>
      endpoints.dismissCopilotFinding(i.id, i.dismissed),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['copilot', 'findings', t] }),
  });
}

export function useCopilotPin() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (i: { id: string; pinned: boolean }) => endpoints.pinCopilotFinding(i.id, i.pinned),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['copilot', 'findings', t] }),
  });
}

/**
 * Ask a question.
 *
 * Invalidates the INVESTIGATIONS as well as the findings, because an ask writes
 * to both: it always raises the current investigation's `findingCount`, and when
 * none is open it CREATES one. Refreshing only the findings left the header
 * naming the previous investigation while the new finding rendered beneath it —
 * and, if the previous one had just been archived, naming nothing at all while
 * an answer sat on the screen. The header and the list have to agree; that they
 * did not is the defect this whole surface keeps rediscovering.
 */
export function useCopilotAsk() {
  const qc = useQueryClient();
  const t = useTenantKey();
  return useMutation({
    mutationFn: (body: CopilotAskRequestInput) => endpoints.copilotAsk(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['copilot', 'findings', t] });
      void qc.invalidateQueries({ queryKey: ['copilot', 'investigations', t] });
    },
  });
}

/**
 * The day's news.
 *
 * ⚠️ Keyed by the DATE as well as the tenant. Without it, moving between days
 * would serve the previous day's news under the new day's heading — a cache hit
 * that looks exactly like a fast load, and on this page the date IS the subject.
 */
export function useDailyHeadlines(date: string | undefined, asTenantId: string | null = null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['daily-headlines', asTenantId ?? t, date ?? 'today'],
    queryFn: () =>
      asTenantId ? endpoints.dailyHeadlinesAs(asTenantId, date) : endpoints.dailyHeadlines(date),
    // Today moves as the day does; a recorded day never changes.
    staleTime: date ? 60 * 60_000 : 60_000,
    retry: false,
  });
}

/**
 * Writing in the journal.
 *
 * ⚠️ Every one invalidates the DAY, not a notes key of its own. The notes come
 * back with the day's headlines deliberately — a note has no meaning apart from
 * the day it is about — so a separate cache entry would let the two drift and
 * show yesterday's notes under today's news.
 */
export function useFleetNewsNotes(date: string | undefined) {
  const client = useQueryClient();
  const t = useTenantKey();
  const invalidate = () =>
    client.invalidateQueries({ queryKey: ['daily-headlines', t, date ?? 'today'] });

  return {
    add: useMutation({
      mutationFn: (body: CreateFleetNewsNote) => endpoints.addFleetNewsNote(body),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      /*
       * ⚠️ `noteType` is passed through as given, including `undefined`. The
       * server reads an omitted field as "leave the type alone" and an explicit
       * null as "clear it back to General"; coalescing here would retype a note
       * to General every time somebody corrected a typo.
       */
      mutationFn: (input: { id: string; body: string; noteType?: FleetNewsNoteType | null }) =>
        endpoints.editFleetNewsNote(input.id, { body: input.body, noteType: input.noteType }),
      onSuccess: invalidate,
    }),
    withdraw: useMutation({
      mutationFn: (id: string) => endpoints.withdrawFleetNewsNote(id),
      onSuccess: invalidate,
    }),
  };
}

/**
 * A vehicle's tachograph activity, day by day.
 *
 * ⚠️ Enabled only once a vehicle is chosen. A query that fired with no id would
 * be a request the page cannot use, and `enabled` is what keeps the card's
 * mount the fetch rather than the page's — the pattern the partner hierarchy
 * tree established and which is asserted as a NEGATIVE, because that is the
 * half that silently stops being true.
 */
export function useTachoActivity(vehicleId: string | null, days: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['tacho-activity', t, vehicleId, days],
    queryFn: () => endpoints.tachoActivity(vehicleId!, { days }),
    enabled: vehicleId !== null,
    retry: false,
  });
}

/**
 * The vehicles a tachograph activity chart can be drawn for.
 *
 * ⚠️ Its own query rather than a slice of `useFleetTacho`, because the windows
 * differ — that one is a fixed 24 hours. Sourcing the picker from it hid the
 * chart on any fleet whose devices had been quiet for a day.
 */
export function useTachoVehicles(days: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['tacho-vehicles', t, days],
    queryFn: () => endpoints.tachoVehicles({ days }),
    retry: false,
  });
}

export function useFleetTacho(asTenantId: string | null = null) {
  const t = useTenantKey();
  return useQuery({
    /*
     * ⚠️ Keyed by the tenant being READ, not the one in context. Without the
     * branch id in the key, switching branch on the dashboard would serve the
     * previous branch's answer under the new branch's name — a cache hit that
     * looks exactly like a fast load.
     */
    queryKey: ['fleet-tacho', asTenantId ?? t],
    queryFn: () => (asTenantId ? endpoints.fleetTachoAs(asTenantId) : endpoints.fleetTacho()),
    retry: false,
  });
}

export function useFleetSummary(hours: number) {
  const t = useTenantKey();
  return useQuery({
    queryKey: fleetKeys.summary(t, hours),
    queryFn: () => endpoints.fleetSummary({ hours }),
    placeholderData: (prev) => prev,
  });
}

/**
 * The two attention counts the sidebar draws beside `Incidents` and `Recommendations`.
 *
 * ⚠️ **THE SAME QUERY KEY AS `useFleetSummary(168)`, deliberately, so the badge is usually
 * FREE.** React Query shares one cache entry between every caller of a key, and 168 is both
 * `FLEET_SUMMARY_DEFAULT_HOURS` — the dashboard's own default period — and the AI Assistant
 * rail's fixed window. So on the two pages that already read this, the menu adds no request at
 * all; elsewhere it adds one small one. A key of its own would double the call on exactly the
 * pages where it is most likely to be open.
 *
 * ⚠️ **GUARDED ON HAVING A TENANT, which `useFleetSummary` is NOT — and the sidebar is the
 * caller that makes the difference matter.** That hook is mounted only inside a tenant, so it
 * never needed one; this renders on EVERY page, including a platform administrator standing
 * outside every tenant and a partner in their own console. `useTenantKey()` answers `'none'`
 * there, so without `enabled` the menu would issue a refused request on every page load for
 * precisely the readers who have no fleet to count.
 *
 * ⚠️ **`retry: false`, because a badge must never become an incident of its own.** The figure is
 * decoration on a navigation control: a failed read draws nothing, exactly as a withheld one
 * does, and re-asking three times spends a connection on a number nobody is waiting for. The
 * pages themselves report their own failures, where a reader can act on them.
 */
export function useNavCounts(): NavCounts {
  const t = useTenantKey();
  const { data } = useQuery({
    queryKey: fleetKeys.summary(t, NAV_COUNT_HOURS),
    queryFn: () => endpoints.fleetSummary({ hours: NAV_COUNT_HOURS }),
    enabled: t !== 'none',
    placeholderData: (prev) => prev,
    retry: false,
  });
  /*
   * ⚠️ `?? null` folds THREE states into one — not loaded, not entitled, and WITHHELD (a closed
   * past period, which this rolling window never asks for but which the contract permits). They
   * all draw the same absent badge, so the menu cannot tell them apart and must not pretend to.
   */
  return {
    incidents: data?.openNow ?? null,
    /*
     * ⚠️ **`undecidedRecommendations`, NOT `awaitingApproval` — the badge shipped reading the
     * wrong one and a customer found it.** A queue of three drew a badge of 1: `awaitingApproval`
     * counts only advice needing a PRIVILEGED decision, so the second `New` row — which any
     * operator could have acknowledged — was silently dropped. The dashboard tile beside it is
     * still `awaitingApproval` and is CORRECT there: it is labelled "Awaiting approval" and says
     * what it counts, where a menu badge says only "Recommendations".
     */
    recommendations: data?.undecidedRecommendations ?? null,
  };
}

/**
 * One branch's summary, read from the organisation.
 *
 * Keyed by the BRANCH, not by the tenant in context, so switching between
 * branches in the scope selector does not serve one branch's figures under
 * another's name from the cache.
 */
/**
 * One branch's recent incidents and recommendations, read from the
 * organisation. Keyed by the BRANCH so switching between them in the scope
 * selector cannot serve one branch's rows under another's name.
 */
export function useIncidentsFor(tenantId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['incidents-as', tenantId],
    queryFn: () =>
      endpoints.incidentsAs(tenantId!, { pageSize: 6, sort: 'lastEventAt', direction: 'desc' }),
    enabled: enabled && !!tenantId,
  });
}

export function useRecommendationsFor(tenantId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['recommendations-as', tenantId],
    queryFn: () =>
      endpoints.recommendationsAs(tenantId!, { pageSize: 6, sort: 'createdAt', direction: 'desc' }),
    enabled: enabled && !!tenantId,
  });
}

export function useFleetSummaryFor(tenantId: string | null, hours: number, enabled: boolean) {
  return useQuery({
    queryKey: ['fleet-summary-as', tenantId, hours],
    queryFn: () => endpoints.fleetSummaryAs(tenantId!, { hours }),
    enabled: enabled && !!tenantId,
    placeholderData: (prev) => prev,
  });
}

/**
 * The same figures, combined across every sub-tenant of the active PARENT.
 *
 * A separate hook rather than a flag on the one above, because the shapes differ
 * in a way that matters: the consolidated response carries `withheld`, and a
 * caller that treated the two as interchangeable would drop it — showing a blank
 * median with no explanation of why.
 *
 * `enabled` because the route is refused outside a parent context, which is
 * where most sessions are.
 */
export function useConsolidatedFleetSummary(hours: number, enabled: boolean) {
  const t = useTenantKey();
  return useQuery({
    queryKey: [...fleetKeys.summary(t, hours), 'consolidated'],
    queryFn: () => endpoints.consolidatedFleetSummary({ hours }),
    placeholderData: (prev) => prev,
    enabled,
    retry: false,
  });
}

/**
 * CAN operations figures.
 *
 * Polled, because the live readings panel is the point: a throttle position
 * from whenever the tab was opened is worse than none. Thirty seconds matches
 * the typical provider reporting interval, so the page moves at roughly the
 * rate the data does.
 */
export function useCanOperations(
  days: number,
  vehicleId?: string,
  asTenantId: string | null = null,
) {
  const t = useTenantKey();
  return useQuery({
    queryKey: fleetKeys.canOperations(asTenantId ?? t, days, vehicleId),
    queryFn: () => {
      const q = { days, ...(vehicleId ? { vehicleId } : {}) };
      return asTenantId ? endpoints.canOperationsAs(asTenantId, q) : endpoints.canOperations(q);
    },
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
    retry: false,
  });
}

export function usePipelineHealth() {
  return useQuery({
    queryKey: platformKeys.pipelineHealth,
    queryFn: () => endpoints.pipelineHealth(),
    refetchInterval: 60_000,
    // A snapshot is worth nothing once stale, so refetch on return to the tab.
    refetchOnWindowFocus: true,
  });
}

/**
 * Sub-tenants of the active PARENT.
 *
 * `enabled` matters here: the routes are refused with 403 from a sub-tenant
 * context, and an unconditional query would fire one on every page load for the
 * majority of sessions — which land in a sub-tenant by design.
 */
export function useSubTenants(enabled: boolean, includeArchived = false) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['sub-tenants', t, includeArchived],
    queryFn: () => endpoints.subTenants(includeArchived),
    enabled,
  });
}

/**
 * Sub-tenant mutations.
 *
 * PESSIMISTIC, like every other governance action: the cap, the name rule that
 * is unique WITHIN the parent, and the initial-administrator requirement are all
 * server rules the client cannot evaluate.
 */
export function useSubTenantMutations() {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['sub-tenants'] });
    // The switcher's contents come from `me`, which AuthProvider owns rather
    // than react-query — so refreshing it is the caller's job, not an
    // invalidation. Without it the branch exists and cannot be reached.
    void refreshUser();
  };
  return {
    create: useMutation({
      mutationFn: (body: Parameters<typeof endpoints.createSubTenant>[0]) =>
        endpoints.createSubTenant(body),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; body: Parameters<typeof endpoints.updateSubTenant>[1] }) =>
        endpoints.updateSubTenant(v.id, v.body),
      onSuccess: invalidate,
    }),
    /**
     * Suspend, reactivate, archive or restore.
     *
     * PESSIMISTIC like the rest: the server refuses things the client cannot
     * evaluate — the Default sub-tenant is protected, an archived branch cannot
     * be suspended, and a restore returns it SUSPENDED rather than active.
     */
    lifecycle: useMutation({
      mutationFn: (v: {
        id: string;
        action: 'suspend' | 'reactivate' | 'archive' | 'restore';
        note?: string;
      }) => endpoints.subTenantLifecycle(v.id, v.action, v.note ? { note: v.note } : {}),
      onSuccess: () => {
        invalidate();
        // Archiving a branch removes a destination from the switcher, and
        // restoring one adds it back.
        void qc.invalidateQueries({ queryKey: ['vehicles'] });
      },
    }),
  };
}

/**
 * Move a vehicle to a sibling sub-tenant.
 *
 * PESSIMISTIC, and more than usually so: the server refuses on registration
 * collisions the client cannot see (a reserved plate held by an ARCHIVED vehicle
 * in the destination) and on authority the client cannot evaluate.
 */
export function useVehicleTransfer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toTenantId: string) => endpoints.transferVehicle(id, { toTenantId }),
    onSuccess: () => {
      // The vehicle has left this tenant, so every list that showed it is wrong.
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
      void qc.invalidateQueries({ queryKey: ['vehicle', id] });
      void qc.invalidateQueries({ queryKey: ['sub-tenants'] });
    },
  });
}

/**
 * Vehicles a provider keeps reporting that nobody here has created.
 *
 * Polled rarely rather than live: the underlying rows are aggregated counters
 * updated by ingestion, and a fleet page that refetched them constantly would
 * spend more requests on the exception than on the fleet.
 */
/**
 * Run a DECLARED query.
 *
 * ⚠️ **The document must be a stable value, not built in render.** It is part
 * of the query key, and an object literal recreated every render would be a new
 * key every render — a refetch loop that looks like a slow dashboard. Every
 * caller in this app holds its document in a module constant for that reason,
 * which is also what makes "the client never authors a query" true by
 * construction rather than by promise.
 */
export function useDeclaredQuery(
  key: string,
  query: QueryDocumentInput,
  asTenantId: string | null = null,
  enabled = true,
) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['declared-query', key, asTenantId ?? t],
    queryFn: () =>
      asTenantId ? endpoints.runQueryAs(asTenantId, query) : endpoints.runQuery(query),
    enabled,
    // A refusal is an answer the card renders; retrying three times would only
    // slow the page down on a tenant that is never going to be allowed.
    retry: false,
    placeholderData: (prev) => prev,
  });
}

export function useUnresolvedVehicles(asTenantId: string | null = null) {
  const t = useTenantKey();
  return useQuery({
    queryKey: ['unresolved-vehicles', asTenantId ?? t],
    queryFn: () =>
      asTenantId ? endpoints.unresolvedVehiclesAs(asTenantId) : endpoints.unresolvedVehicles(),
    staleTime: 60_000,
    // A VIEWER cannot read this; failing quietly is right, because the panel
    // simply does not render for them.
    retry: false,
  });
}

/*
 * ──────────────────────────────────────────────────────────────────────────────
 * The internal log store (§5)
 *
 * ⚠️ **NO `useTenantKey()` in any of these keys, unlike every other hook in this
 * file.** The log surface is platform-scoped and takes no tenant context, so
 * keying on the active tenant would make the same search refetch on a switch and
 * — worse — cache one answer per tenant for a query whose answer does not depend
 * on one. `tenantId` is a FILTER here, and it is already part of the query.
 * ──────────────────────────────────────────────────────────────────────────────
 */

export function useLogSearch(q: Record<string, unknown>, enabled = true) {
  return useQuery({
    queryKey: ['logs', 'search', q],
    queryFn: () => logsApi.search(q),
    enabled,
    /*
     * ⚠️ Not retried. A refused search is refused for a reason the reader has to
     * act on — no time range, an unrecognised filter, no log store configured —
     * and retrying a 400 three times just delays the message.
     */
    retry: false,
  });
}

export function useLogLine(ref: string | null) {
  return useQuery({
    queryKey: ['logs', 'line', ref],
    queryFn: () => logsApi.line(ref!),
    enabled: !!ref,
    retry: false,
  });
}

export function useLogTimeline(input: { correlationId: string; around: string } | null) {
  return useQuery({
    queryKey: ['logs', 'timeline', input],
    queryFn: () => logsApi.timeline(input as unknown as Record<string, unknown>),
    enabled: !!input,
    retry: false,
  });
}

export function useLogDashboard(window: string) {
  return useQuery({
    queryKey: ['logs', 'dashboard', window],
    queryFn: () => logsApi.dashboard(window),
    /*
     * ⚠️ Refetched on the SUMMARY's cadence, not faster. The figures are rebuilt
     * every five minutes by a scheduled job, so polling harder would re-fetch
     * the same numbers and make the page look live when it is not — and the
     * response carries `refreshedAt` precisely so the reader knows the
     * difference.
     */
    refetchInterval: 60_000,
    retry: false,
  });
}

// ── §1 runtime log levels ───────────────────────────────────────────────────

/**
 * ⚠️ **NOT POLLED, unlike every other panel on that page.** The dashboard's
 * figures come from five-minute summary tables and are refetched on that cadence;
 * this is the state of the process answering right now, and it changes only when
 * somebody presses something here or an override expires. Polling it would make
 * a control surface flicker for no new information. `staleTime: 0` so reopening
 * the page after a change elsewhere reads the truth rather than a cached copy.
 */
export function useLogLevels() {
  return useQuery({
    queryKey: ['platform', 'log-levels'],
    queryFn: () => logLevelsApi.state(),
    staleTime: 0,
    retry: false,
  });
}

/**
 * The logger names the answering replica holds.
 *
 * ⚠️ A TYPING AID, never a permitted set — see `levelConcerns`. Cached for the
 * session: the set changes only when the process is replaced.
 */
export function useLogLevelServices() {
  return useQuery({
    queryKey: ['platform', 'log-levels', 'services'],
    queryFn: () => logLevelsApi.services(),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * ⚠️ **BOTH MUTATIONS WRITE THE RETURNED STATE STRAIGHT INTO THE CACHE.** The
 * server answers with the state it now holds, so `setQueryData` is the only
 * reading that cannot disagree with what actually happened — an invalidate-and-
 * refetch would briefly show the previous state on the one control whose whole
 * question is whether the change took effect.
 */
export function useSetLogLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LogLevelSetRequest) => logLevelsApi.set(body),
    onSuccess: (state) => qc.setQueryData(['platform', 'log-levels'], state),
  });
}

export function useClearLogLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (service: string) => logLevelsApi.clear(service),
    onSuccess: (state) => qc.setQueryData(['platform', 'log-levels'], state),
  });
}

// ── §7 platform alerts ──────────────────────────────────────────────────────

export function usePlatformAlerts(status: 'live' | 'all') {
  return useQuery({
    queryKey: ['platform', 'alerts', status],
    queryFn: () => platformAlertsApi.list({ status }),
    /*
     * ⚠️ Polled on the EVALUATOR's cadence, not faster. Nothing on this page is
     * computed on read — the rows are whatever the last run left behind — so
     * polling harder would re-fetch identical rows and make the queue look live
     * when it is not. `lastEvaluatedAt` is what actually tells the reader how
     * current this is.
     */
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useDecidePlatformAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, act }: { id: string; act: 'acknowledge' | 'resolve' }) =>
      act === 'acknowledge' ? platformAlertsApi.acknowledge(id) : platformAlertsApi.resolve(id),
    /*
     * ⚠️ **PESSIMISTIC, like every other governance action here.** The server
     * refuses a second decision with a 409, and an optimistic update would show
     * the alert acknowledged to somebody whose press was rejected because a
     * colleague got there first — which is the one thing that control must never
     * do.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'alerts'] }),
  });
}
